import React from "react";
import ListItem from "@material-ui/core/ListItem";

import ListItemText from "@material-ui/core/ListItemText";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import List from "@material-ui/core/List";

import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";
import Collapse from "@material-ui/core/Collapse";
import makeStyles from "@material-ui/core/styles/makeStyles";

import { DefaultCheckbox as Checkbox } from "components/form/checkbox";
import { isEmpty } from "lodash";

export interface IMultiSelectDataNode {
    value: string;
    label: string;
    children?: IMultiSelectDataNode[];
}

export interface IMultiSelectOptions {
    data: IMultiSelectDataNode[];
    onUpdate(value: Object): void;
    selectedValues: string[];
}

const NOT_SELECTED = 0;
const PARTIALLY_SELECTED = 1;
const SELECTED = 2;

interface INestedListItem {
    value: string;
    label: string;
    data: IMultiSelectDataNode[];
    onCheck: (string) => void;
    selectedValues: object;
    nested?: boolean;
}

export const NestedListItem: React.FC<INestedListItem> = ({
    selectedValues,
    value,
    label,
    data,
    onCheck,
    nested = false,
}) => {
    const [isOpen, setIsOpen] = React.useState(false);

    const classes = useStyles();

    const handleClick = React.useCallback(() => {
        setIsOpen(!isOpen);
    }, [isOpen]);

    const handleOnChange = React.useCallback(() => {
        onCheck(value);
        setIsOpen(!isOpen);
    }, [onCheck, isOpen, value]);

    const expandLessOrMore = React.useMemo(
        () => (isOpen ? <ExpandLess onClick={handleClick} /> : <ExpandMore onClick={handleClick} />),
        [handleClick, isOpen]
    );

    return (
        <>
            <ListItem>
                <ListItemIcon>
                    <Checkbox
                        name={value}
                        checked={selectedValues[value] === SELECTED || selectedValues[value] === PARTIALLY_SELECTED}
                        indeterminate={selectedValues[value] === PARTIALLY_SELECTED}
                        onChange={handleOnChange}
                    />
                </ListItemIcon>
                <ListItemText primary={label} />
                {!!data?.length && <ListItemIcon>{expandLessOrMore}</ListItemIcon>}
            </ListItem>
            {!!data?.length && (
                <div className={classes.nested}>
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {data.map(({ value: childValue, label: childLabel, children }) => (
                                <NestedListItem
                                    key={`${value}::${childValue}`}
                                    selectedValues={selectedValues}
                                    onCheck={onCheck}
                                    nested
                                    label={childLabel}
                                    value={`${value}::${childValue}`}
                                    data={children}
                                />
                            ))}
                        </List>
                    </Collapse>
                </div>
            )}
        </>
    );
};

export const Multiselect: React.FC<IMultiSelectOptions> = ({ data = [], onUpdate, selectedValues = [] }) => {
    const [valueNodesKeys, setValueNodesKeys] = React.useState<string[]>([]);
    const [selectedStateValues, setSelectedStateValues] = React.useState<Record<string, number>>({});

    const classes = useStyles();

    React.useEffect(() => {
        // selectedStateData includes parent node's selection state, when selectedValues - only child nodes
        const selectedValuesObject = selectedValues.reduce((acc, key) => {
            acc[key] = SELECTED;
            return acc;
        }, {});
        const { selectedStateData, nodesWithoutChildren } = getDefaultSelectedState(data, selectedValuesObject);
        // keep child nodes, because in nested tree only their values matters, parent node state is a combination of child nodes states
        setValueNodesKeys(nodesWithoutChildren);
        // populate selected values with available keys
        setSelectedStateValues(selectedStateData);
    }, [data, selectedValues]);

    const onCheck = React.useCallback(
        (changeKey) => {
            const newState = selectedStateValues[changeKey] === NOT_SELECTED ? SELECTED : NOT_SELECTED;
            const updatedValues = Object.keys(selectedStateValues).reduce((acc, key) => {
                let _value = selectedStateValues[key];
                // Update filter and related to parent filter subfilter in case it has
                if (key.startsWith(changeKey + "::") || changeKey === key) {
                    _value = newState;
                }
                // Filter on selected filter
                if (valueNodesKeys.includes(key) && _value === SELECTED) {
                    return [...acc, key];
                }
                return acc;
            }, []);
            onUpdate(updatedValues);
        },
        [selectedStateValues, valueNodesKeys, onUpdate]
    );

    return (
        <div className={classes.wrapper}>
            {data.map(({ value, label, children }) => (
                <NestedListItem
                    key={value}
                    data={children}
                    label={label}
                    selectedValues={selectedStateValues}
                    onCheck={onCheck}
                    value={value}
                />
            ))}
        </div>
    );
};

export default Multiselect;

function getDefaultSelectedState(data = [], defaultSelectedData = {}, parentKey = null) {
    let selectedStateData = { ...defaultSelectedData };

    let nodesWithoutChildren = [];

    function getCompositeKey(key) {
        return parentKey ? `${parentKey}::${key}` : key;
    }

    data.forEach((node) => {
        const key = getCompositeKey(node.value);
        if (!isEmpty(node.children)) {
            const { selectedStateData: childrenSelectedState, nodesWithoutChildren: childrenNodesWithoutChildren } =
                getDefaultSelectedState(node.children, defaultSelectedData, key);
            selectedStateData = { ...selectedStateData, ...childrenSelectedState };
            nodesWithoutChildren = [...nodesWithoutChildren, ...childrenNodesWithoutChildren];

            const selectedChildrenCount = childrenNodesWithoutChildren.filter(
                (nodeKey) => childrenSelectedState[nodeKey]
            ).length;
            if (selectedChildrenCount > 0) {
                selectedStateData[key] =
                    selectedChildrenCount === childrenNodesWithoutChildren.length ? SELECTED : PARTIALLY_SELECTED;
            }
        } else {
            nodesWithoutChildren.push(key);
        }
        selectedStateData[key] = selectedStateData[key] || NOT_SELECTED;
    });

    return { selectedStateData, nodesWithoutChildren };
}

const useStyles = makeStyles((theme) => ({
    root: {
        width: "100%",
        maxWidth: 360,
        backgroundColor: theme.palette.background.paper,
    },
    nested: {
        paddingLeft: theme.spacing(2),
    },
    wrapper: {
        maxHeight: "400px",
    },
}));
